import { createAsyncThunk } from '@reduxjs/toolkit';
import { Paginated, PaginationService } from '../../services/api-handlers/pagination';
import { Reward, QueryParams } from '../../types';
import SortingService from '../../services/sorting';
import { RewardApi } from '../../services/end-points';
import { resolveApiCall } from '../../services/api-handlers/api-resolver';
import { makeFormErrorsFromResponse } from '../../components/react-hook-form/utils/make-form-errors';
import { ThunkConfig } from '../configure-store';
import { StoreState } from '../configure-store';
import { toast } from 'react-toastify';

export const listRewards = createAsyncThunk<Paginated<Reward>, QueryParams, ThunkConfig>(
    'reward/list',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { reward } = getState() as StoreState;
        const { filters, sorting, ...rest } = payload;

        return PaginationService.resolveApiCall(
            thunkApi,
            reward,
            async () =>
                RewardApi.list({
                    ...filters,
                    ...SortingService.makeOrder(sorting),
                    ...rest,
                }),
            payload.page,
            reward.data,
        );
    },
);

export const getReward = createAsyncThunk<Reward, { id: string; onError: (data: any) => any }, ThunkConfig>(
    'reward/current',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { reward } = getState() as StoreState;
        const { id, onError } = payload;

        return resolveApiCall(thunkApi, reward, async () => (await RewardApi.get(id)).data, onError);
    },
);

export const deleteReward = createAsyncThunk<any, { id: string; callback: () => void }, ThunkConfig>(
    'reward/delete',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { id } = payload;
        const { reward } = getState() as StoreState;
        await resolveApiCall(
            thunkApi,
            reward,
            async () => await RewardApi.remove(id),
            async (err: any) => {
                const message = err?.response?.data?.message || 'Failed to delete reward.';
                toast.error(message);
                return err;
            },
        );
        payload.callback();
    },
);

export const createReward = createAsyncThunk<Reward, any, ThunkConfig>('reward/create', async (payload, thunkApi) => {
    const { getState } = thunkApi;
    const { reward } = getState() as StoreState;
    return resolveApiCall(
        thunkApi,
        reward,
        async () => await RewardApi.create(payload),
        async (err) => {
            const { response } = err;
            return makeFormErrorsFromResponse(response.data);
        },
    );
});

export const updateReward = createAsyncThunk<Reward, { id: Reward['id']; data: Partial<any> }, ThunkConfig>(
    'reward/update',
    async (payload, thunkApi) => {
        const { getState } = thunkApi;
        const { reward } = getState() as StoreState;
        const { id, data } = payload;
        return resolveApiCall(
            thunkApi,
            reward,
            async () => await RewardApi.update(id, data),
            async (err) => {
                const { response } = err;
                return makeFormErrorsFromResponse(response.data);
            },
        );
    },
);
